/**
 * Helper: String
 * -----------------------------------------------------------------------------
 * Retrieve langauge string from theme strings.
 *
 * @param {String} path - Path to string defined locales file, e.g.
 * `accessibility.close`, if this path exists it will be automatically added to
 * `theme-compiled-strings.liquid`.
 * @param {Object} [options] - Options object.
 * @param {Boolean} [options.decode] - Use decodeURIComponent() on replacement
 * strings, only applies when `replace` is used.
 * @param {Number} [options.pluralise] - Number used to determine whether the
 * single or plural string is used in the provided path.
 * @param {Object} [options.replace] - Key: value pairs of text to replace in
 * the string, e.g. { label: 'New' } would replace `{{ label }}` in the string.
 * @returns {String}
 *
 */
import { handlePluralisation } from '~/helpers/general'

export default (path, {
  decode = true,
  pluralise,
  replace = {},
} = {}) => {
  let compiledString = cnvs.compiledStrings || {}
  let customString = cnvs.strings || {}
  let missingString = false

  /**
   * Go through cnvs.compiledStrings and cnvs.strings.
   */
  for (const part of path.split('.')) {
    if (!customString[part] && !compiledString[part]) {
      missingString = true
      break
    }

    compiledString = compiledString[part] ? compiledString[part] : false
    customString = customString[part] ? customString[part] : false
  }

  /**
   * Define string, cnvs.strings should override cnvs.compiledStrings.
   */
  let string = false

  if (
    typeof compiledString === 'string' ||
    typeof compiledString === 'object'
  ) {
    string = compiledString
  }

  if (
    typeof customString === 'string' ||
    typeof customString === 'object'
  ) {
    string = customString
  }

  /**
   * Throw error if no string found
   */
  if (missingString || !string) {
    window.console.warn(`No string found at 'cnvs.compiledStrings.${path}' or 'cnvs.strings.${path}'`)
    return 'No string found'
  }

  /**
   * If pluralise then update string to use single or plural items.
   * - Assumes provided path is object in format { single: '', plural: '' }
   */
  const correctStringType =
    typeof string === 'object' || string.includes('=&gt;')

  if (typeof pluralise !== 'number' && correctStringType) {
    window.console.warn(`Object found at 'cnvs.compiledStrings.${path}' or 'cnvs.strings.${path}'`)
    return '[ Object ]'
  }

  if (typeof pluralise === 'number' && correctStringType) {
    if (typeof string !== 'object') {
      string = string
        .replace(/[=]&gt;/g, ':')
        .replace(/&quot;/g, '"')

      string = JSON.parse(string)
    }

    string = handlePluralisation(string, pluralise)
  }

  /**
   * Go through each each replacement and replace string with value.
   */
  if (Object.keys(replace).length) {
    Object.keys(replace).forEach((key) => {
      const regex = new RegExp(`{{ ${key} }}`, 'g')

      const replaceKey = decode
        ? decodeURIComponent(replace[key])
        : replace[key]

      string = string.replace(regex, replaceKey)
    })
  }

  /**
   * Replace escaped characters.
   */
  string = string
    .replace(/&#39;/g, '\'')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/[=]&gt;/g, ':')

  return string
}
